import React, { createContext, useContext, useEffect, useRef } from 'react';
import useNotifications from '../hooks/useNotifications';
import { useToast } from '../components/ToastContainer';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { showInfo } = useToast();
  const previousUnreadCount = useRef(0);
  
  const notificationData = useNotifications();
  const { unreadCount, notifications } = notificationData;

  // Détecter les nouvelles notifications et afficher des toasts
  useEffect(() => {
    if (!isAuthenticated) return;

    // Première fois ou pas de changement
    if (previousUnreadCount.current === 0 || unreadCount === previousUnreadCount.current) {
      previousUnreadCount.current = unreadCount;
      return;
    }

    // Nouvelles notifications détectées
    if (unreadCount > previousUnreadCount.current) {
      const newNotificationsCount = unreadCount - previousUnreadCount.current;
      
      // Trouver les nouvelles notifications (les plus récentes)
      const newNotifications = notifications
        .filter(notif => !notif.is_read)
        .slice(0, newNotificationsCount);

      // Afficher une toast pour la notification la plus récente
      if (newNotifications.length > 0) {
        const latestNotification = newNotifications[0];
        
        // Son de notification (optionnel)
        playNotificationSound();
        
        // Toast notification
        showInfo(
          `${latestNotification.title}${newNotificationsCount > 1 ? ` (+${newNotificationsCount - 1} autres)` : ''}`,
          {
            duration: 5000,
            onClick: () => {
              // Ouvrir le centre de notifications ou naviguer vers la notification
              console.log('Notification clicked:', latestNotification);
            }
          }
        );
      }
    }

    previousUnreadCount.current = unreadCount;
  }, [unreadCount, notifications, isAuthenticated, showInfo]);

  // Fonction pour jouer un son de notification
  const playNotificationSound = () => {
    try {
      // Créer un son simple avec Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Fréquence en Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      // Fallback silencieux si Web Audio API n'est pas disponible
      console.log('Could not play notification sound:', error);
    }
  };

  // Fonction pour marquer le navigateur tab comme ayant des notifications
  const updatePageTitle = (count) => {
    const originalTitle = 'WaaGo Admin';
    if (count > 0) {
      document.title = `(${count}) ${originalTitle}`;
    } else {
      document.title = originalTitle;
    }
  };

  // Mettre à jour le titre de la page
  useEffect(() => {
    updatePageTitle(unreadCount);
    
    // Nettoyer le titre au démontage
    return () => {
      document.title = 'WaaGo Admin';
    };
  }, [unreadCount]);

  // Notification de bureau (avec permission)
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  };

  const showDesktopNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const desktopNotif = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico', // Icône de l'application
        badge: '/favicon.ico',
        tag: notification.id, // Éviter les doublons
      });

      // Auto-fermer après 5 secondes
      setTimeout(() => desktopNotif.close(), 5000);

      // Gérer le clic sur la notification
      desktopNotif.onclick = () => {
        window.focus();
        desktopNotif.close();
        // Ici on peut naviguer vers la notification
      };
    }
  };

  // Fonction pour demander les permissions de notification
  useEffect(() => {
    if (isAuthenticated) {
      requestNotificationPermission();
    }
  }, [isAuthenticated]);

  const contextValue = {
    ...notificationData,
    requestNotificationPermission,
    showDesktopNotification,
    playNotificationSound
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};